<template>
  <div class="software_theme_panel" :style="softwareThemeStudioStyle">
    <!-- 实时预览（仅设备选择页展示，设备详情页可直接看到实际效果）-->
    <div v-if="showPreview" class="theme_live_preview">
      <div class="preview_mock_header">
        <span class="mock_pill"></span>
        <span class="mock_pill wide"></span>
        <span class="mock_orb"></span>
      </div>
      <div class="preview_mock_body">
        <div class="preview_mock_sidebar">
          <span class="mock_sidebar_item active"></span>
          <span class="mock_sidebar_item"></span>
          <span class="mock_sidebar_item"></span>
        </div>
        <div class="preview_mock_canvas">
          <div class="mock_canvas_card highlight"></div>
          <div class="mock_canvas_row">
            <div class="mock_canvas_card"></div>
            <div class="mock_canvas_card soft"></div>
          </div>
          <div class="mock_canvas_actions">
            <span class="mock_btn primary"></span>
            <span class="mock_btn"></span>
            <span class="mock_btn ghost"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- 预设 -->
    <section class="theme_section">
      <div class="palette_grid">
        <button type="button" class="palette_card" :class="{ active: softwareTheme.mode === 'system' }" @click="useSystemTheme">
          <div class="palette_preview_v2" :style="{ backgroundColor: 'var(--software-theme-system-swatch-deep)' }">
            <span class="preset_key" :style="{ backgroundColor: 'var(--software-theme-system-swatch-soft)' }"></span>
            <span class="preset_key" :style="{ backgroundColor: 'var(--software-theme-system-swatch-brand)' }"></span>
          </div>
          <div class="palette_name">{{ $t('software_theme_default') }}</div>
        </button>

        <button v-for="option in softwareThemeOptions" :key="option.value" type="button" class="palette_card" :class="{ active: softwareTheme.mode === 'preset' && softwareTheme.preset === option.value }" @click="setSoftwareThemePreset(option.value)">
          <div class="palette_preview_v2" :style="{ backgroundColor: option.swatches[1] }">
            <span class="preset_key" :style="{ backgroundColor: option.swatches[0] }"></span>
            <span class="preset_key" :style="{ backgroundColor: option.swatches[2] }"></span>
          </div>
          <div class="palette_name">{{ $t(option.labelKey) }}</div>
        </button>

        <button type="button" class="palette_card" :class="{ active: softwareTheme.mode === 'custom' }" @click="useCustomTheme">
          <div class="palette_preview_v2 custom_v2" :style="{ backgroundColor: softwareThemeCustomPreviewSwatches[1] }">
            <span class="preset_key" :style="{ backgroundColor: softwareThemeCustomPreviewSwatches[0] }"></span>
            <span class="preset_key accent" :style="{ backgroundColor: softwareThemeCustomPreviewSwatches[2] }"></span>
          </div>
          <div class="palette_name">{{ $t('software_theme_custom_colors') }}</div>
        </button>
      </div>
    </section>

    <!-- 自定义颜色 -->
    <section class="theme_section" :class="{ custom_active: softwareTheme.mode === 'custom' }">
      <div class="section_header">
        <div class="section_title">{{ $t('software_theme_custom_colors') }}</div>
        <div v-if="softwareTheme.mode !== 'custom'" class="section_hint" @click="useCustomTheme">{{ $t('software_theme_click_to_activate') }}</div>
        <div v-else class="section_active_badge">{{ $t('software_theme_active') }}</div>
      </div>

      <div class="custom_color_grid">
        <label v-for="field in softwareThemeColorFields" :key="field.key" class="color_control_card">
          <span class="control_label">{{ $t(field.labelKey) }}</span>
          <span class="control_picker">
            <span class="control_swatch" :style="{ backgroundColor: softwareTheme.customColors[field.key] }"></span>
            <input class="native_color_input" type="color" :value="softwareTheme.customColors[field.key]" @input="handleCustomColorInput(field.key, $event)" />
          </span>
          <input
            class="native_hex_input"
            type="text"
            :value="colorDrafts[field.key]"
            maxlength="7"
            spellcheck="false"
            @input="handleColorDraftInput(field.key, $event)"
            @blur="commitColorDraft(field.key)"
            @keydown.enter.prevent="commitColorDraft(field.key)"
          />
        </label>

        <div class="slider_controls">
          <div v-for="field in softwareThemeSliderFields" :key="field.key" class="slider_row">
            <span class="slider_label">{{ $t(field.labelKey) }}</span>
            <input
              class="custom_slider_input"
              type="range"
              min="0"
              max="100"
              :value="softwareTheme.customSettings[field.key]"
              :style="{ backgroundSize: `${softwareTheme.customSettings[field.key]}% 100%` }"
              @input="handleCustomSliderInput(field.key, $event)"
            />
            <span class="slider_val">{{ softwareTheme.customSettings[field.key] }}%</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 自定义背景图片 -->
    <section class="theme_section bg_section">
      <div class="section_header">
        <div class="section_title">{{ $t('software_bg_title') }}</div>
        <div v-if="customBackground.enabled" class="section_active_badge">{{ $t('software_theme_active') }}</div>
      </div>

      <p class="bg_hint">{{ $t('software_bg_desc') }}</p>

      <div class="bg_actions">
        <label class="bg_upload_btn" :class="{ disabled: customBackgroundBusy }">
          <input ref="bgFileInput" type="file" accept="image/jpeg,image/png,image/webp,image/gif" :disabled="customBackgroundBusy" @change="handleBackgroundFileChange" />
          <span v-if="customBackgroundBusy">{{ $t('software_bg_processing') }}</span>
          <span v-else>{{ customBackground.enabled ? $t('software_bg_replace') : $t('software_bg_upload') }}</span>
        </label>
        <button v-if="customBackground.enabled" type="button" class="bg_clear_btn" :disabled="customBackgroundBusy" @click="handleClearBackground">{{ $t('software_bg_clear') }}</button>
      </div>

      <p v-if="bgError" class="bg_error">{{ bgError }}</p>

      <div v-if="customBackground.enabled" class="bg_fill_modes">
        <button
          v-for="mode in backgroundFillModes"
          :key="mode.value"
          type="button"
          class="bg_fill_card"
          :class="{ active: customBackground.fillMode === mode.value }"
          @click="setCustomBackgroundFillMode(mode.value)"
        >
          <span class="bg_fill_icon" :class="`mode_${mode.value}`"></span>
          <span class="bg_fill_name">{{ $t(mode.labelKey) }}</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import i18n from '@/languages/i18n'

const t = (key: string) => i18n.global.t(key)
import {
  setSoftwareThemeCustomColors,
  setSoftwareThemeCustomSettings,
  setSoftwareThemeMode,
  setSoftwareThemePreset,
  softwareTheme,
  softwareThemeColorFields,
  softwareThemeCustomPreviewSwatches,
  softwareThemeOptions,
  softwareThemeSliderFields,
  softwareThemeStudioStyle,
  type SoftwareThemeColorField,
  type SoftwareThemeSliderField,
} from '@/service/software_theme'
import {
  clearCustomBackground,
  customBackground,
  customBackgroundBusy,
  setCustomBackgroundFillMode,
  setCustomBackgroundFromFile,
  type CustomBackgroundFillMode,
} from '@/service/custom_background'

withDefaults(
  defineProps<{
    showPreview?: boolean
  }>(),
  {
    showPreview: true,
  },
)

const useSystemTheme = () => {
  setSoftwareThemeMode('system')
}

const useCustomTheme = () => {
  setSoftwareThemeMode('custom')
}

const colorDrafts = reactive<Record<string, string>>({})
softwareThemeColorFields.forEach((f) => {
  colorDrafts[f.key] = softwareTheme.customColors[f.key]
})

watch(
  () => softwareTheme.customColors,
  (colors) => {
    softwareThemeColorFields.forEach((f) => {
      colorDrafts[f.key] = colors[f.key]
    })
  },
  { deep: true },
)

const handleCustomColorInput = (field: SoftwareThemeColorField, event: Event) => {
  const target = event.target as HTMLInputElement
  setSoftwareThemeCustomColors({ [field]: target.value })
}

const handleColorDraftInput = (field: SoftwareThemeColorField, event: Event) => {
  const target = event.target as HTMLInputElement
  colorDrafts[field] = target.value.trim()
}

const commitColorDraft = (field: SoftwareThemeColorField) => {
  setSoftwareThemeCustomColors({ [field]: colorDrafts[field] })
  colorDrafts[field] = softwareTheme.customColors[field]
}

const handleCustomSliderInput = (field: SoftwareThemeSliderField, event: Event) => {
  const target = event.target as HTMLInputElement
  setSoftwareThemeCustomSettings({ [field]: Number(target.value) })
}

const backgroundFillModes: Array<{ value: CustomBackgroundFillMode; labelKey: string }> = [
  { value: 'cover', labelKey: 'software_bg_mode_cover' },
  { value: 'contain', labelKey: 'software_bg_mode_contain' },
  { value: 'center', labelKey: 'software_bg_mode_center' },
]

const bgFileInput = ref<HTMLInputElement | null>(null)
const bgError = ref('')

const handleBackgroundFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  bgError.value = ''
  const result = await setCustomBackgroundFromFile(file)
  // 清空 input.value，确保再次选择同一文件也能触发 change。
  input.value = ''

  if (!result.ok) {
    if (result.reason === 'too_large') {
      bgError.value = t('software_bg_gif_too_large')
    } else if (result.reason === 'unsupported') {
      bgError.value = t('software_bg_unsupported')
    } else if (result.reason === 'storage_failed') {
      bgError.value = t('software_bg_storage_failed')
    } else {
      bgError.value = t('software_bg_decode_failed')
    }
  }
}

const handleClearBackground = async () => {
  bgError.value = ''
  await clearCustomBackground()
}
</script>

<style scoped lang="less">
.software_theme_panel {
  max-width: 860px;
  margin: 0 auto;
  height: 100%;
  box-sizing: border-box;
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
    background: transparent !important;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: var(--radius-pill);
    background: rgba(0, 0, 0, 0.1);
  }
}

/* ── 实时预览 ─────────────────────────────── */

.theme_live_preview {
  padding: 14px;
  border-radius: var(--radius-l);
  background: var(--bg-main);
  border: 1px solid rgba(255, 255, 255, 0.32);
  display: flex;
  flex-direction: column;
  gap: 18px;
  contain: paint;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 12px 28px rgba(19, 32, 51, 0.08);
}

.preview_mock_header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mock_pill {
  width: 68px;
  height: 12px;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.6);

  &.wide {
    width: 108px;
  }
}

.mock_orb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-left: auto;
  background: var(--software-theme-button-bg);
}

.preview_mock_body {
  display: flex;
  gap: 8px;
  min-height: 72px;
}

.preview_mock_sidebar {
  width: 44px;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.14);
  padding: 7px 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-shrink: 0;
}

.mock_sidebar_item {
  width: 100%;
  height: 9px;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.36);

  &.active {
    background: var(--software-theme-button-bg);
  }
}

.preview_mock_canvas {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.mock_canvas_row {
  display: flex;
  gap: 7px;
}

.mock_canvas_card {
  width: 100%;
  height: 40px;
  border-radius: var(--radius-m);
  background: var(--software-theme-surface-soft);
  border: 1px solid rgba(255, 255, 255, 0.64);

  &.highlight {
    height: 48px;
    background: linear-gradient(135deg, var(--software-theme-accent-soft) 0%, var(--software-theme-surface-strong) 100%);
  }

  &.soft {
    background: rgba(255, 255, 255, 0.62);
  }
}

.mock_canvas_actions {
  display: flex;
  gap: 7px;
}

.mock_btn {
  height: 9px;
  flex: 1;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.44);

  &.primary {
    background: var(--software-theme-button-bg);
  }

  &.ghost {
    background: var(--software-theme-accent-soft);
  }
}

/* ── 通用 Section（对应 keyboard_appearance_section）── */

.theme_section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: var(--radius-l);
  background: var(--glass-surface-bg);
  backdrop-filter: var(--glass-surface-blur);
  border: 1px solid rgba(255, 255, 255, 0.48);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.04);
}

.section_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section_title {
  font-size: 14px;
  font-weight: 700;
  color: var(--surface-text-primary);
}

.section_hint {
  font-size: 12px;
  font-weight: 600;
  color: var(--brand-color-pure);
  opacity: 0.6;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  transition: var(--transition-base);

  &:hover {
    opacity: 1;
    background: rgba(var(--color-brand-1-rgb), 0.08);
  }
}

.section_active_badge {
  font-size: 12px;
  font-weight: 700;
  color: var(--brand-color-pure);
  background: rgba(var(--color-brand-1-rgb), 0.12);
  border-radius: var(--radius-pill);
  padding: 4px 10px;
  white-space: nowrap;
}

.theme_section.custom_active {
  border-color: rgba(var(--color-brand-2-rgb), 0.22);
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(var(--color-brand-1-rgb), 0.1);
}

/* ── 预设网格（对应 keyboard_appearance palette_grid）── */

.palette_grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

/* palette_card */
.palette_card {
  border: none;
  padding: 12px;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: var(--transition-base);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  min-height: 92px;
  text-align: left;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid var(--brand-color-pure);
    outline-offset: 2px;
  }

  &.active {
    background: rgba(var(--color-brand-1-rgb), 0.14);
    box-shadow:
      inset 0 0 0 1.5px rgba(var(--color-brand-1-rgb), 0.55),
      0 12px 28px rgba(var(--color-brand-1-rgb), 0.18);
    transform: translateY(-1px);

    .palette_name {
      color: var(--brand-color-pure);
    }
  }
}

/* palette_preview_v2: 对应 keyboard_appearance 完全一致 */
.palette_preview_v2 {
  width: 100%;
  height: 38px;
  border-radius: var(--radius-m);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 6px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all var(--transition-base);
  &.custom_v2 {
    border: 1.5px dashed rgba(var(--color-brand-1-rgb), 0.3);
  }
}

/* preset_key: 对应 keyboard_appearance �?v2_key */
.preset_key {
  width: 18px;
  height: 18px;
  border-radius: var(--radius-s);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &.accent {
    width: 22px;
    height: 22px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

/* palette_name: �?keyboard_appearance 完全一�?*/
.palette_name {
  font-size: 12px;
  font-weight: 700;
  color: var(--surface-text-primary);
  line-height: 1.4;
  word-break: break-word;
}

/* ── 自定义颜色网格（对齐 keyboard_appearance custom_color_grid）── */

.custom_color_grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

/* color_control_card: �?keyboard_appearance 完全一致（含嵌套子类）*/
.color_control_card {
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.78);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: var(--transition-base);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }

  .control_label {
    font-size: 12px;
    font-weight: 700;
    color: var(--surface-text-primary);
  }

  .control_picker {
    position: relative;
    width: 100%;
    height: 38px;
    border-radius: var(--radius-m);
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  .control_swatch,
  .native_color_input {
    width: 100%;
    height: 100%;
    display: block;
  }

  .control_swatch {
    pointer-events: none;
  }

  .native_color_input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }

  .native_hex_input {
    width: 100%;
    height: 36px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: var(--radius-m);
    background: var(--color-gray-1);
    padding: 0 10px;
    font-size: 12px;
    font-weight: 700;
    color: var(--surface-text-primary);
    text-transform: uppercase;
    transition: var(--transition-base);

    &:focus {
      outline: none;
      border-color: var(--brand-color-pure);
      box-shadow: 0 0 0 4px rgba(var(--color-brand-2-rgb), 0.1);
    }
  }
}

/* ── 滑块（与之前一致）─────────────────────── */

.slider_controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.slider_row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.slider_label {
  font-size: 12px;
  font-weight: 700;
  color: var(--surface-text-primary);
  flex-shrink: 0;
  white-space: nowrap;
}

.slider_val {
  font-size: 12px;
  font-weight: 700;
  color: var(--software-theme-accent);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
  padding: 3px 8px;
  border-radius: var(--radius-pill);
  background: var(--software-theme-accent-soft);
}

.custom_slider_input {
  flex: 1;
  appearance: none;
  height: 6px;
  border-radius: var(--radius-pill);
  background-color: rgba(0, 0, 0, 0.05);
  background-image: linear-gradient(var(--brand-color-pure), var(--brand-color-pure));
  background-repeat: no-repeat;
  background-position: left center;

  &::-webkit-slider-runnable-track {
    height: 6px;
    border-radius: var(--radius-pill);
    background: transparent;
  }

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    margin-top: -5px;
    border-radius: 50%;
    border: 2px solid #fff;
    background: var(--brand-color-pure);
    box-shadow: 0 6px 16px rgba(var(--color-brand-1-rgb), 0.28);
    transition: transform var(--transition-base);
  }

  &:hover::-webkit-slider-thumb {
    transform: scale(1.12);
  }
}

/* ── 自定义背景图片 ─────────────────────────── */

.bg_hint {
  font-size: 12px;
  line-height: 1.6;
  color: var(--surface-text-primary);
  opacity: 0.7;
  margin: 0;
}

.bg_actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.bg_upload_btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: var(--radius-pill);
  background: var(--brand-color-pure);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition-base);
  box-shadow: 0 6px 16px rgba(var(--color-brand-1-rgb), 0.28);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(var(--color-brand-1-rgb), 0.34);
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  input[type='file'] {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
}

.bg_clear_btn {
  padding: 9px 16px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.7);
  color: var(--surface-text-primary);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition-base);

  &:hover {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.18);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.bg_error {
  font-size: 12px;
  font-weight: 600;
  color: #e5484d;
  margin: 0;
}

.bg_fill_modes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.bg_fill_card {
  border: none;
  padding: 12px;
  border-radius: var(--radius-m);
  background: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: var(--transition-base);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }

  &.active {
    background: rgba(var(--color-brand-1-rgb), 0.14);
    box-shadow:
      inset 0 0 0 1.5px rgba(var(--color-brand-1-rgb), 0.55),
      0 12px 28px rgba(var(--color-brand-1-rgb), 0.18);

    .bg_fill_name {
      color: var(--brand-color-pure);
    }
  }
}

.bg_fill_icon {
  width: 100%;
  height: 40px;
  border-radius: var(--radius-s);
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(var(--color-brand-1-rgb), 0.12);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    border-radius: 3px;
    background: var(--brand-color-pure);
    opacity: 0.55;
  }

  &.mode_cover::after {
    inset: 0;
  }

  &.mode_contain::after {
    inset: 8px 18px;
  }

  &.mode_center::after {
    top: 50%;
    left: 50%;
    width: 40%;
    height: 50%;
    transform: translate(-50%, -50%);
  }
}

.bg_fill_name {
  font-size: 12px;
  font-weight: 700;
  color: var(--surface-text-primary);
}

/* ── 响应�?─────────────────────────────── */

@media (max-width: 960px) {
  .custom_color_grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
